import { Checkbox, CircularProgress, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FormikValues, useField, useFormikContext } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import useDebounce from '../../shared/hooks/UseDebounce';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { useSnackbar } from 'notistack';
import { AxiosPromise } from 'axios';
import useTSHacks from '../../shared/tshacks/tshacks';
import ApiService from '../../services/ApiService';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const { getObjectProperty } = useTSHacks();

export interface ApiAutocompleteProps {
    fieldName: string;
    fieldLabel: string;
    optionsMethod: (searchTerm?: string, values?: any) => AxiosPromise<Array<any>>;
    getByIdMethod?: (id: number) => AxiosPromise<any>;
    fieldValidator?: (value: any) => string;
    required?: boolean;
    disabled?: boolean;
    multiple?: boolean;
}

const ApiAutocomplete = ({
    fieldName,
    fieldLabel,
    fieldValidator,
    optionsMethod,
    getByIdMethod,
    required,
    disabled,
    multiple,
    ...props
}: ApiAutocompleteProps) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const debouncedSearchTerm = useDebounce(inputValue, 500);
    const { enqueueSnackbar } = useSnackbar();

    const [options, setOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const { values } = useFormikContext();

    const [{ value }, meta, { setTouched, setValue }] = useField({
        name: fieldName,
        required,
        validate: fieldValidator,
    });

    useEffect(() => {
        const fetchData = async () => {
            const selectedIds = getObjectProperty(values, fieldName);
            if (selectedIds && getByIdMethod) {
                setLoading(true);
                const itemsSelected: any[] = [];
                await Promise.all(
                    selectedIds.map(async (id: string) => {
                        try {
                            itemsSelected.push((await getByIdMethod(parseInt(id))).data);
                        } catch (e) {
                            enqueueSnackbar('Unable to load ' + fieldLabel + ' field options', { variant: 'error' });
                        }
                    }),
                );
                setOptions(itemsSelected);
                setValue(itemsSelected)
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const fieldOptions = async (searchTerm: string): Promise<Array<any>> => {
        try {
            return (await optionsMethod(searchTerm, values)).data;
        } catch (e) {
            enqueueSnackbar('Unable to load ' + fieldLabel + ' field options', { variant: 'error' });
        }
        return [];
    }

    useEffect(() => {
        let active = true;

        if (debouncedSearchTerm) {
            setLoading(true);
            fieldOptions(debouncedSearchTerm)
                .then((opts) => {
                    if (active) {
                        setOptions(opts);
                    }
                })
                .finally(() => setLoading(false));
        }

        return (): void => {
            active = false;
        };
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    return (
        <Autocomplete
            {...props}
            disableCloseOnSelect={multiple}
            renderOption={(option, { selected }) => (
                <Fragment>
                    {multiple ? <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} /> : null}
                    {option.udk}
                </Fragment>
            )}
            multiple={multiple as true}
            disabled={disabled}
            onBlur={(): void => setTouched(true)}
            onChange={(_: React.ChangeEvent<{}>, value: any | null): void => setValue(value)}
            value={value}
            open={open}
            onOpen={(): void => setOpen(true)}
            onClose={(): void => setOpen(false)}
            options={options}
            loading={loading}
            getOptionLabel={(option: any): string => option.udk || option.toString()}
            onInputChange={(_: React.ChangeEvent<{}>, value: string): void => setInputValue(value)}
            renderInput={(params): React.ReactNode => (
                <TextField
                    {...params}
                    label={required ? (fieldLabel + ' *') : fieldLabel}
                    variant="filled"
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error ? meta.error : null}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default ApiAutocomplete;